import { ApiResponse } from 'apisauce'
import { call, put, select } from 'redux-saga/effects'

import api from 'src/services/api'
import objectToQueryString from 'src/helpers/object-to-query-string'

import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'

import { PAGE_SIZE_EDIT, PAGE_SIZE_SEARCH } from '.'
import { ResourcesHttpData } from './types'
import * as Actions from './actions'

type ParamsType = {
  [key: string]: string | number | boolean
}

export function* getResources({
  payload: {
    options = {
      allowAll: false,
      page: 1
    },
    resource,
    isEdit = false
  }
}: ReturnType<typeof Actions.getResources>): unknown {
  try {
    const {
      me: { organization },
      resources
    } = yield select((state: ApplicationState) => state)

    const pageSize = isEdit ? PAGE_SIZE_EDIT : PAGE_SIZE_SEARCH
    const organization_id =
      options?.organization_id || organization.organization_id

    if (
      !isEdit &&
      resources[resource].organization_id === organization_id &&
      resources[resource].allowAll === options.allowAll &&
      resources[resource].total_results === resources[resource].list.length &&
      resources[resource].total_results <= pageSize &&
      resources[resource].total_results > 0
    ) {
      yield put(Actions.finishLoader(resource))
      return
    }

    let params: ParamsType = {}

    // We can't have item edit and all
    if (!isEdit && options.allowAll) {
      params.all = 'True'
    }

    if (isEdit) {
      params.admin = 'True'
    }

    delete options.allowAll
    params = Object.assign(params, options, {
      page_size: pageSize
    })

    const querystring = objectToQueryString(params)

    const { data, ok }: ApiResponse<ResourcesHttpData> = yield call(
      api.get,
      `organizations/${organization_id}/resources/${resource}${querystring}`
    )

    if (ok) {
      yield put(
        Actions.getResourcesSuccess({
          allowAll: !!params.all,
          list: !data[resource] ? [] : data[resource],
          organization_id,
          page: data.page,
          resource,
          total_pages: data.total_pages,
          total_results: data.total_results
        })
      )
      return
    }

    yield put(Actions.getResourcesFailure(resource))
  } catch (err) {
    ClientNotifyCatch('Saga getResources', err)
    yield put(Actions.getResourcesFailure(resource))
  }
}

export function* reloadResourcesPage({
  payload: { resource }
}: ReturnType<typeof Actions.reloadResourcesPage>): unknown {
  try {
    const {
      me: {
        organization: { organization_id }
      },
      resources
    } = yield select((state: ApplicationState) => state)

    const pageSize = PAGE_SIZE_EDIT
    let { page } = resources[resource]
    do {
      const { data, ok }: ApiResponse<ResourcesHttpData> = yield call(
        api.get,
        `organizations/${organization_id}/resources/${resource}?page_size=${pageSize}&page=${page}`
      )

      if (ok) {
        const hasItems = data[resource]?.length > 0
        if (hasItems || page === 1) {
          yield put(
            Actions.getResourcesSuccess({
              list: data[resource] || [],
              organization_id,
              page: data.page,
              resource,
              total_pages: data.total_pages,
              total_results: data.total_results
            })
          )
          return
        }
      }

      yield put(Actions.reloadResourcesPageFailure(resource, (page -= 1)))
    } while (page > 1)

    yield put(Actions.getResourcesFailure(resource))
  } catch (err) {
    ClientNotifyCatch('Saga reloadResourcesPage', err)
    yield put(Actions.getResourcesFailure(resource))
  }
}

export function* setResources({
  payload: { resource, values }
}: ReturnType<typeof Actions.setResources>) {
  try {
    const {
      me: {
        organization: { organization_id }
      }
    } = yield select((state: ApplicationState) => state)

    const isStandard = values.some(({ standard }) => standard)

    const { data, ok }: ApiResponse<ResourcesHttpData> = yield call(
      api.post,
      `organizations/${organization_id}/resources/${resource}${
        isStandard ? '?force=true' : ''
      }`,
      {
        [resource]: values
      }
    )

    if (ok) {
      const value = data[resource][0]
      yield put(Actions.setResourcesSuccess(resource, value))
      return
    }

    yield put(Actions.setResourcesFailure(resource))
  } catch (err) {
    ClientNotifyCatch('Saga setResources', err)
    yield put(Actions.setResourcesFailure(resource))
  }
}
